import { CHART_TITLE_ID, GRAPH_CANVAS_ID, TABLE_ID } from "../../constants/reports_constants";
import Chart from 'chart.js/auto'
import { getChartGenerator, hideReportSpinner } from "packs/reports/reports";
import autocolors from 'chartjs-plugin-autocolors';

Chart.register(autocolors)

export default class ReportGenerator {

    /**
     * Get the argus--table controller for the report table element.
     * @returns {Object|null}
     */
    _getArgusTable() {
        const el = document.getElementById(TABLE_ID)
        if (!el || !window.Stimulus) return null
        return window.Stimulus.getControllerForElementAndIdentifier(el, 'argus--table')
    }

    /**
     * Hide a column by field name.
     */
    _hideColumn(field) {
        const argus = this._getArgusTable()
        if (argus) { argus.hideColumn(field) }
    }

    /**
     * Show a column by field name.
     */
    _showColumn(field) {
        const argus = this._getArgusTable()
        if (argus) { argus.showColumn(field) }
    }

    /**
     * Display report from store data (new store-based approach)
     * 
     * Uses the same callback pattern as the API-based approach to support
     * report-specific aggregation logic via _tableAggregator and chart
     * generation via _createChart.
     * 
     * @param {Array} devices - Devices from filteredItemsStore
     */
    displayReportFromStore(devices) {
        this._updateChartTitle();
        this._hideUnusedColumns();
        this._showUsedColumns();
        this._clearTable();
        this._clearGraph();
        
        // Get the filter function for this report (if any)
        const filterFunction = this._getFilterFunction();
        
        // Get the chart generator for this report type with filter function
        const chartGenerator = getChartGenerator(this._getSelectedReportTitle(), filterFunction);
        
        // Process devices using the same callback pattern as API-based approach
        this._processDevicesFromStore(
            devices,
            this._createChart.bind(this),
            chartGenerator,
            this._tableAggregator.bind(this)
        );
    }
    
    /**
     * Get the filter function for this report type
     * 
     * Override in subclasses to provide report-specific filtering.
     * Returns null by default (no filtering).
     * 
     * @returns {Function|null} Filter function or null
     * @protected
     */
    _getFilterFunction() {
        return null;
    }
    
    /**
     * Process devices from store and update table and chart
     * 
     * Uses callback pattern to support report-specific aggregation logic.
     * Each report type can override _tableAggregator to customize how
     * devices are displayed in the table.
     * 
     * @param {Array} devices - Devices to process
     * @param {Function} createChartCallback - Function to create chart (_createChart)
     * @param {Object} chartGenerator - Chart generator for this report type
     * @param {Function} tableDecoratorCallback - Function to populate table (_tableAggregator)
     * @private
     */
    _processDevicesFromStore(devices, createChartCallback, chartGenerator, tableDecoratorCallback) {
        // Apply report-specific filtering if provided
        const filterFunction = this._getFilterFunction();
        const filteredDevices = filterFunction ? devices.filter(filterFunction) : devices;
        
        // Apply report-specific table aggregation
        // This allows each report type to customize how devices are displayed
        tableDecoratorCallback(filteredDevices);
        
        // Create chart with aggregated data
        createChartCallback(filteredDevices, chartGenerator);
        
        // Hide the loading spinner
        hideReportSpinner();
    }

    _clearGraph() {
        // Get the Chart element. This will be undefined if one hasn't been rendered yet.
        const chart = Chart.getChart(GRAPH_CANVAS_ID);
        if (chart !== undefined) {
            chart.destroy();
        }
    }

    _clearTable() {
        const tableElement = document.getElementById(TABLE_ID);
        if (tableElement) {
            const app = window.Stimulus
            if (app) {
                const argusTable = app.getControllerForElementAndIdentifier(tableElement, 'argus--table')
                if (argusTable) {
                    argusTable.clearData()
                    return
                }
            }
        }
    }

    _getSelectedReportTitle() {
        return $("input[name='report_button']:checked").val();
    }

    _updateChartTitle() {
        const graphTitle = this._getGraphTitle();
        const graphTitleHtmlElement = document.getElementById(CHART_TITLE_ID);

        // Update the graph title
        graphTitleHtmlElement.innerText = graphTitle;
    }

    _getGraphTitle() {
        throw Error('This method is supposed to be overridden!');
    }

    _hideUnusedColumns() {
        throw Error('This method is supposed to be overridden!');
    }

    _showUsedColumns() {
        throw Error('This method is supposed to be overridden!');
    }

    _getChartType() {
        throw Error('This method is supposed to be overridden!');
    }



    /**
     * Filter to include only devices (exclude parts)
     * 
     * A device is identified by having a non-null/empty device.device_type value.
     * 
     * @param {Array} items - Array of devices and/or parts
     * @returns {Array} Array containing only devices
     * @protected
     */
    _filterDevicesOnly(items) {
        return items.filter(item => item?.device?.device_type);
    }

    /**
     * Filter to include only parts (exclude devices)
     * 
     * A part is identified by having a non-null/empty part.part_type value.
     * 
     * @param {Array} items - Array of devices and/or parts
     * @returns {Array} Array containing only parts
     * @protected
     */
    _filterPartsOnly(items) {
        return items.filter(item => item?.part?.part_type);
    }

    /**
     * This implementation simply adds each device to the table as its own row. Some reports
     * want to show data on the table aggregated at a higher level.
     *
     * @param devices an array of device metadata, as JSON
     * @private
     */
    _tableAggregator(devices) {
        const tableElement = document.getElementById(TABLE_ID);
        if (tableElement) {
            const app = window.Stimulus
            if (app) {
                const argusTable = app.getControllerForElementAndIdentifier(tableElement, 'argus--table')
                if (argusTable) {
                    argusTable.loadData(devices)
                    return
                }
            }
        }
    }

    /**
     * This method is not intended to be overridden. It renders the chart using
     * If you need a new ChartGenerator, create one in the getChartPresenter method in app/javascript/packs/reports/reports.js
     *
     * @param matchingDevices an array of devices that match the report's conditions.
     * @param chartGenerator a ChartGenerator class that tells us [1] what type of chart to render and [2] what data to display.
     * @private
     */

    _createChart(matchingDevices, chartGenerator) {
        if (chartGenerator.isChartEnabled()) {
            new Chart(
                document.getElementById(GRAPH_CANVAS_ID),
                {
                    type: chartGenerator.getChartType(),
                    options: {
                        animation: true,
                        plugins: {
                            legend: {
                                display: chartGenerator.getShowLegend()
                            },
                            tooltip: {
                                enabled: true
                            },
                            autocolors: {
                                mode: 'data'
                            },
                        },
                        layout: {
                            padding: 20
                        },
                    },
                    data: {
                        datasets: [{
                            data: chartGenerator.getChartData(matchingDevices)
                        }],
                        labels: chartGenerator.getChartLabels(matchingDevices)
                    }
                }
            );

            hideReportSpinner();
        } else {
            hideReportSpinner();
        }
    }

}